import React from 'react';
import { View, Text, TextInput, ScrollView, TouchableOpacity, Image, Platform } from 'react-native';
import { Icon as RNIcon } from 'react-native-elements';
import { useFocusEffect, useIsFocused } from '@react-navigation/core';
import { useTranslation } from 'react-i18next';
import useChangeShoppingCart from 'hooks/useChangeShoppingCart';
import useChangeOrder from 'hooks/useChangeOrder';
import ShoppingCartModal from 'components/ShoppingCartModal';
import OrderPendingMessage from 'components/OrderPendingMessage';
import AppContext from 'contexts/AppContext';
import OrderContext from 'contexts/OrderContext';
import ProductImageTypes from 'types/enums/ProductImageTypes';
import ProductDetailScreenProps from 'types/screens/ProductDetailScreenProps';
import ShoppingCart from 'types/models/ShoppingCart';
import { styles } from 'styles/screens/productDetailScreen';
import { greenColor } from 'styles/global';
import VenueImageTypes from 'types/enums/VenueImageTypes';
import App from 'constants/App';
import CommerceHeader from 'components/CommerceHeader';
import DetailItem from 'types/models/DetailItem';
import Product, { ClothingSizeTypeStock, OrderRelatedProduct } from 'types/models/Product';
import { valueToCurrencyFormat } from 'utils/parseCurrency';
import ProductType from 'types/enums/ProductType';
import { getGuanicionesExtrasProduct } from 'services/productsService';
import GuarnicionesExtrasModal from 'components/GuarnicionesExtrasModal';
import GuarnicionExtraItem from 'components/GuarnicionExtraItem';

const groupArray = (arr: ClothingSizeTypeStock[], size: number) => {
  const newArray = [];
  for (let i = 0; i < arr.length; i += size) {
    newArray.push(arr.slice(i, i + size));
  }
  return newArray;
};

export default ({ navigation }: ProductDetailScreenProps): JSX.Element => {
  const { t, i18n } = useTranslation();
  const isFocused = useIsFocused();

  const { modalizeData, setModalizeData, currentVenue } = React.useContext(AppContext);
  const { currentCommerce, currentProduct, currentShoppingCart, setCurrentShoppingCart } =
    React.useContext(OrderContext);
  const { storeCurrentShoppingCart } = useChangeShoppingCart();
  const { deleteCurrentOrder } = useChangeOrder();

  const [productQuantity, setQuantity] = React.useState<number>(1);
  const [commentTemplate, setCommentTemplate] = React.useState('');
  const [guarnicionesAndExtras, setGuarnicionesAndExtras] = React.useState<Product[]>([]);
  const [selectedGuarnicionesAndExtras, setSelectedGuarnicionesAndExtras] = React.useState<DetailItem[]>([]);
  const [actionsEnabled, setActionsEnabled] = React.useState<boolean | undefined>(undefined);
  const [currentClothingSize, setCurrentClothingSize] = React.useState<{
    id: number | undefined;
    stock: number;
  }>({
    id: currentProduct?.clothingSizeStocks.filter(({ stock }) => stock !== 0)[0]?.id ?? undefined,
    stock: currentProduct?.clothingSizeStocks.filter(({ stock }) => stock !== 0)[0]?.stock ?? 0,
  });

  const productImage = currentProduct?.images.find(
    (image) => image.productImageTypeId === ProductImageTypes.logo,
  )?.path;
  const venueLogo =
    currentVenue?.images.find((image: any) => image.venueImageId === VenueImageTypes.logo)?.path ?? App.defaultImage;

  const sizeOptions = groupArray(currentProduct?.clothingSizeStocks ?? [], 4);

  const handleFetchGuarnicionesAndExtras = async () => {
    if (!currentProduct) return;
    if (currentProduct.typeId !== ProductType.WExtra && currentProduct.typeId !== ProductType.WGuarnicion) {
      setActionsEnabled(true);
      return;
    }

    setActionsEnabled(false);
    try {
      let items = guarnicionesAndExtras;
      if (guarnicionesAndExtras.length === 0) {
        items = await getGuanicionesExtrasProduct(currentProduct.id);
        setGuarnicionesAndExtras(items);
      }
      if (items.length === 0) {
        setActionsEnabled(true);
      }
    } catch (err) {
      setActionsEnabled(true);
    }
  };

  const handleAddGuarnicionOrExtra = (guarnicionExtra: Product, mainProduct: Product) => {
    const cartHasProducts = currentShoppingCart && currentShoppingCart.detail.length > 0;

    /**
     * Incrementa la cantidad del producto relacionado, o lo agrega si no existe
     */
    const updateRelatedProducts = (
      relatedProducts: OrderRelatedProduct[] | undefined,
      mainProductId: string,
    ): OrderRelatedProduct[] => {
      if (!relatedProducts) return [{ id: mainProductId, quantity: 1 }];
      const found = relatedProducts.find((rel) => rel.id === mainProductId);
      if (found) {
        return relatedProducts.map((rel) => (rel.id === mainProductId ? { ...rel, quantity: rel.quantity + 1 } : rel));
      } else {
        return [...relatedProducts, { id: mainProductId, quantity: 1 }];
      }
    };

    /**
     * Actualiza el estado local de guarniciones/extras seleccionados
     */
    const updateSelectedGuarnicionesAndExtras = () => {
      const existing = selectedGuarnicionesAndExtras.find((item) => item.product.id === guarnicionExtra.id);

      if (existing) {
        // Actualizamos la cantidad y los relatedProducts
        setSelectedGuarnicionesAndExtras((prev) =>
          prev.map((item) =>
            item.product.id === guarnicionExtra.id
              ? {
                  ...item,
                  quantity: item.quantity + 1,
                  relatedProducts: updateRelatedProducts(item.relatedProducts, mainProduct.id),
                }
              : item,
          ),
        );
      } else {
        // Agregamos nuevo
        const newSelectedGuarnicionOrExtra = guarnicionesAndExtras.find((p) => p.id === guarnicionExtra.id);
        if (newSelectedGuarnicionOrExtra) {
          const newItem: DetailItem = {
            product: newSelectedGuarnicionOrExtra,
            quantity: 1,
            relatedProducts: [{ id: mainProduct.id, quantity: 1 }],
          };
          setSelectedGuarnicionesAndExtras((prev) => [...prev, newItem]);
        }
      }
    };

    // --- CASO 1: El carrito tiene productos
    if (cartHasProducts) {
      const guarnicionExtraInCart = currentShoppingCart.detail.find((item) => item.product.id === guarnicionExtra.id);

      if (guarnicionExtraInCart) {
        onChangeQuantity(guarnicionExtra.id, guarnicionExtraInCart.quantity + 1);

        // Actualizamos los productos relacionados (onChangeQuantity no lo hace)
        guarnicionExtraInCart.relatedProducts = updateRelatedProducts(
          guarnicionExtraInCart.relatedProducts,
          mainProduct.id,
        );

        setCurrentShoppingCart({ ...currentShoppingCart });
      } else {
        const mainProductInCart = currentShoppingCart.detail.find((item) => item.product.id === mainProduct.id);
        if (mainProductInCart) {
          const newDetail: DetailItem = {
            product: guarnicionExtra,
            quantity: 1,
            relatedProducts: [{ id: mainProduct.id, quantity: 1 }],
          };
          currentShoppingCart.detail.push(newDetail);
          setCurrentShoppingCart({ ...currentShoppingCart });
        }

        // Siempre actualizamos el estado local aunque esté en el carrito
        updateSelectedGuarnicionesAndExtras();
      }
    }
    // --- CASO 2: No hay carrito o está vacío
    else {
      updateSelectedGuarnicionesAndExtras();
    }
  };

  const setupInitialGuarnicionesAndExtras = () => {
    if (!currentShoppingCart || !currentProduct) return;

    const guarnicionesAndExtras = currentShoppingCart.detail.filter(
      (item) =>
        [ProductType.Extra, ProductType.Guarnicion].includes(item.product.typeId) &&
        item.relatedProducts?.some((rel) => rel.id === currentProduct.id),
    );

    setSelectedGuarnicionesAndExtras(guarnicionesAndExtras);
  };

  const openModal = (shoppingCart: ShoppingCart) => {
    if (!currentCommerce || !currentProduct) {
      return;
    }
    setModalizeData({
      content: (
        <ShoppingCartModal
          commerce={currentCommerce}
          product={currentProduct}
          shoppingCart={shoppingCart}
          navigation={navigation}
        />
      ),
      closeOnOverlayTap: false,
    });
  };

  const modalOrderPending = (shoppingCartCommerceCode?: string): JSX.Element => (
    <OrderPendingMessage
      pendingShoppingCartCommerceCode={shoppingCartCommerceCode}
      onRestart={async () => {
        await deleteCurrentOrder();
        await addProductToShoppingCart(true);
      }}
    />
  );

  useFocusEffect(
    React.useCallback(() => {
      if (!currentCommerce) {
        setTimeout(() => {
          navigation.reset({
            index: 0,
            routes: [{ name: 'HomeScreen' }],
          });
        }, 500);
        return;
      }
      if (!currentProduct) {
        setTimeout(() => {
          navigation.reset({
            index: 1,
            routes: [{ name: 'HomeScreen' }, { name: 'CommerceScreen' }],
          });
        }, 500);
        return;
      }
    }, [currentCommerce, currentProduct]),
  );

  useFocusEffect(
    React.useCallback(() => {
      const onFocus = async () => {
        if (currentShoppingCart) {
          await storeCurrentShoppingCart(currentShoppingCart);
        }
      };
      onFocus();
    }, [currentShoppingCart]),
  );

  React.useEffect(() => {
    setupInitialGuarnicionesAndExtras();
    if (isFocused) {
      if (!currentShoppingCart?.detail.find((el) => el.product.id === currentProduct?.id))
        handleFetchGuarnicionesAndExtras();
    }
  }, [isFocused]);

  React.useEffect(() => {
    if (currentProduct && currentShoppingCart) {
      const detailItem = currentShoppingCart.detail.find((item) => item.product.id === currentProduct.id);
      if (detailItem) {
        setQuantity(detailItem.quantity);
      }
    }
  }, [currentShoppingCart]);

  const addGuarnicionesAndExtrasToShoppingCart = (shoppingCart: ShoppingCart) => {
    selectedGuarnicionesAndExtras.forEach((selectedGuarnicionExtra) => {
      const guarnicionExtraInCart = shoppingCart.detail.find(
        (detailItem) => detailItem.product.id === selectedGuarnicionExtra.product.id,
      );

      if (guarnicionExtraInCart) {
        guarnicionExtraInCart.quantity += guarnicionExtraInCart.quantity;

        const existingRelated = guarnicionExtraInCart.relatedProducts?.find((rel) => rel.id === currentProduct?.id);
        if (existingRelated) {
          existingRelated.quantity += existingRelated.quantity;
        }
      } else {
        shoppingCart.detail.push(selectedGuarnicionExtra);
      }
    });
  };

  const onChangeComment = (value: string) => {
    if (!currentShoppingCart || !currentShoppingCart.detail.length) {
      setCommentTemplate(value);
      return;
    }

    const productIsInCart = currentShoppingCart.detail.find((el) => el.product.id === currentProduct?.id);
    if (!productIsInCart) {
      setCommentTemplate(value);
      return;
    }

    const updatedDetail = currentShoppingCart.detail.map((item) => {
      if (item.product.id === currentProduct?.id) {
        return {
          ...item,
          comment: value,
        };
      }
      return item;
    });

    const updatedShoppingCart = {
      ...currentShoppingCart,
      detail: updatedDetail,
    };

    setCurrentShoppingCart(updatedShoppingCart);
  };

  const getCommentValue = () => {
    if (!currentShoppingCart) return commentTemplate;

    const detailItem = currentShoppingCart.detail.find((item) => item.product.id === currentProduct?.id);
    if (detailItem) return detailItem.comment;
    return commentTemplate;
  };

  const addProductToShoppingCart = async (restarting?: boolean) => {
    if (!currentCommerce || !currentProduct) return;

    const selectedClothingSize = currentProduct.clothingSizeStocks.find(({ id }) => id === currentClothingSize.id);
    let shoppingCart = !restarting ? currentShoppingCart : null;
    if (shoppingCart && shoppingCart.commerce.id === currentCommerce.id) {
      //Si currentShoppingCart fue seteado, busca el producto. Si no lo encuentra lo agrega.
      const detailItem = shoppingCart.detail.find((item) => {
        const itsSameProduct = item.product.id === currentProduct.id;
        if (item.product.isClothing) {
          // si es de tipo indumentaria debo buscar el producto que coincida en id y talle
          return itsSameProduct && item.clothingSize?.id === selectedClothingSize?.id;
        } else {
          // Si no es indumentaria entonces solo busco que coincida en el id
          return itsSameProduct;
        }
      });
      if (detailItem) {
        if (detailItem.product.isClothing) {
          if (detailItem.clothingSize?.id === currentClothingSize.id) {
            // Si es el mismo de talle entonces aumento la cantidad
            detailItem.quantity += productQuantity;
          } else {
            //  Si son de distinto talle entonces agrego un item nuevo
            const productItem: DetailItem = {
              product: currentProduct,
              quantity: productQuantity,
              clothingSize: selectedClothingSize,
              comment: getCommentValue(),
            };
            shoppingCart.detail.push(productItem);
          }
        } else {
          // Si encuentro el producto y no es indumentaria entonces agrego eel producto
          detailItem.quantity += productQuantity;
          detailItem.comment = getCommentValue();
        }
      } else {
        const productItem: DetailItem = {
          product: currentProduct,
          quantity: productQuantity,
          comment: getCommentValue(),
        };
        if (currentProduct.isClothing) {
          productItem.clothingSize = selectedClothingSize;
        }
        // Si no lo encuentra lo agrega.
        shoppingCart.detail.push(productItem);
      }
      addGuarnicionesAndExtrasToShoppingCart(shoppingCart);
    } else {
      //Si currentShoppingCart no fue seteado o el comercio del carrito actual no coincide con el comercio del producto, se vacia el carrito
      const detailItem: DetailItem = {
        product: currentProduct,
        quantity: productQuantity,
        comment: getCommentValue(),
      };
      if (currentProduct.isClothing) {
        detailItem.clothingSize = selectedClothingSize;
      }
      shoppingCart = {
        commerce: { id: currentCommerce.id, code: currentCommerce.code, name: currentCommerce.name },
        detail: [detailItem, ...selectedGuarnicionesAndExtras],
        processed: false,
        paid: false,
        phone: '',
        isCash: false,
      };
    }
    setCurrentShoppingCart({ ...shoppingCart });
    openModal(shoppingCart);
  };

  const calculateProductPriceAccordingToQuantity = (): number | undefined => {
    if (currentProduct) {
      return currentProduct.price * productQuantity;
    }
  };

  const onChangeQuantity = (id: string, quantity: number) => {
    if (!currentShoppingCart) return;
    const detailItem = currentShoppingCart.detail.find((item) => item.product.id === id);
    if (detailItem) {
      detailItem.quantity = quantity;
    }
    setCurrentShoppingCart({ ...currentShoppingCart });
  };

  const onClickAdd = async () => {
    if (currentProduct?.isClothing && productQuantity === currentClothingSize.stock) {
      return;
    }
    await handleFetchGuarnicionesAndExtras();
    const newQuantity = productQuantity + 1;
    setQuantity(newQuantity);
    if (currentProduct) onChangeQuantity(currentProduct.id, newQuantity);
  };

  const handleRemoveGuarnicionesAndExtras = (guarnicionExtraDetail?: DetailItem) => {
    if (!currentProduct) return;

    const adjustQuantityOrRemove = (guarnicionExtraId: string) => {
      if (!currentShoppingCart) return;

      const updatedDetail = currentShoppingCart.detail
        .map((item) => {
          if (item.product.id !== guarnicionExtraId) return item;

          const updatedRelated =
            item.relatedProducts?.map((rel) =>
              rel.id === currentProduct.id ? { ...rel, quantity: rel.quantity - 1 } : rel,
            ) ?? [];

          const newQuantity = item.quantity - 1;
          const updatedItem = {
            ...item,
            quantity: newQuantity,
            relatedProducts: updatedRelated.filter((rel) => rel.quantity > 0),
          };

          return newQuantity > 0 && updatedItem.relatedProducts.length > 0 ? updatedItem : null;
        })
        .filter((item) => item !== null);

      setCurrentShoppingCart({
        ...currentShoppingCart,
        detail: updatedDetail,
      });
    };

    const updateSelectedGuarnicionesAndExtras = (guarnicionExtraId: string) => {
      const guarnicionExtra = selectedGuarnicionesAndExtras.find((item) => item.product.id === guarnicionExtraId);
      if (!guarnicionExtra) return;
      setSelectedGuarnicionesAndExtras((prev) =>
        guarnicionExtra.quantity === 1
          ? prev.filter((item) => item.product.id !== guarnicionExtraId)
          : prev.map((item) =>
              item.product.id === guarnicionExtraId
                ? {
                    ...item,
                    quantity: item.quantity - 1,
                    relatedProducts: item.relatedProducts?.map((rel) =>
                      rel.id === currentProduct.id ? { ...rel, quantity: rel.quantity - 1 } : rel,
                    ),
                  }
                : item,
            ),
      );
    };

    // Identificar la guarnición/extra relacionada a restar
    let guarnicionExtraId: string | undefined;
    if (guarnicionExtraDetail) {
      // El usuario quitó directamente la guarnición/extra
      guarnicionExtraId = guarnicionExtraDetail.product.id;
    } else {
      // Se quitó el producto principal → buscar la guarnición/extra relacionada
      const guarnicionExtraThatHasMainProduct = selectedGuarnicionesAndExtras.find((item) =>
        item.relatedProducts?.some((rel) => rel.id === currentProduct.id),
      );

      if (guarnicionExtraThatHasMainProduct) {
        guarnicionExtraId = guarnicionExtraThatHasMainProduct.product.id;
      } else {
        const relatedInCart = currentShoppingCart?.detail.find((item) =>
          item.relatedProducts?.some((rel) => rel.id === currentProduct.id),
        );
        guarnicionExtraId = relatedInCart?.product.id;
      }
    }

    if (guarnicionExtraId) {
      adjustQuantityOrRemove(guarnicionExtraId);
      updateSelectedGuarnicionesAndExtras(guarnicionExtraId);
    }
  };

  const onRemoveProduct = () => {
    const newQuantity = productQuantity - 1;
    setQuantity(newQuantity);
    if (currentProduct) onChangeQuantity(currentProduct.id, newQuantity);
  };

  const onDeleteProduct = (id: string) => {
    if (currentShoppingCart) {
      currentShoppingCart.detail = currentShoppingCart.detail.filter((item) => item.product.id !== id);
      setCurrentShoppingCart({ ...currentShoppingCart });
    }
  };

  const onClickDelete = () => {
    // Si el cantidad de unidades es igual a 1 lo que hacemos es eliminar todo el
    // producto del carrito, si es mayor a 1 simplemente restamos 1
    if (productQuantity === 1 && currentProduct) {
      onDeleteProduct(currentProduct.id);
    } else if (productQuantity > 1) {
      onRemoveProduct();
    }
    handleRemoveGuarnicionesAndExtras();
  };

  const renderControlQuantity = () => {
    return (
      <View style={[styles.controlQuantityContainer, { borderColor: currentVenue?.appColor ?? greenColor }]}>
        <TouchableOpacity onPress={onClickDelete}>
          <RNIcon
            name={productQuantity === 1 ? 'delete' : 'remove'}
            color={currentVenue?.appColor ?? greenColor}
            size={22}
          />
        </TouchableOpacity>

        <Text style={[styles.quantityNumber, { color: currentVenue?.appColor ?? greenColor }]}>{productQuantity}</Text>
        <TouchableOpacity onPress={onClickAdd}>
          <RNIcon name="add" color={currentVenue?.appColor ?? greenColor} size={22} />
        </TouchableOpacity>
      </View>
    );
  };

  const checkSize = (clothingSizeId: number) => {
    if (!currentProduct) return;
    const selectedClothingSize = currentProduct.clothingSizeStocks.find(({ id }) => id === clothingSizeId);
    if (selectedClothingSize) {
      setCurrentClothingSize({
        id: selectedClothingSize.id,
        stock: selectedClothingSize.stock,
      });

      setQuantity(1);
    }
  };

  return (
    <ScrollView style={styles.container} scrollEnabled={!modalizeData}>
      {currentCommerce !== null && (
        <CommerceHeader
          commerceName={currentCommerce.name}
          commerceCode={currentCommerce.code}
          showBackButton={true}
          onBack={() => {
            if (navigation.canGoBack()) {
              if (currentCommerce) {
                navigation.reset({
                  index: 0,
                  routes: [
                    {
                      name: 'CommerceScreen',
                      params:
                        Platform.OS === 'web'
                          ? {
                              venueCode: currentVenue?.code,
                              commerceCode: currentCommerce.code,
                            }
                          : undefined,
                    },
                  ],
                });
              } else {
                navigation.reset({
                  index: 0,
                  routes: [{ name: 'HomeScreen' }],
                });
              }
            }
          }}
          navigation={navigation}
        />
      )}

      <View style={styles.productContainer}>
        <View style={styles.productImageContainer}>
          <Image
            resizeMode="cover"
            source={{
              uri: productImage ?? 'https://via.placeholder.com/700/300',
            }}
            style={styles.productImage}
          />
          <View style={styles.venueLogoContainer}>
            <Image resizeMode="center" source={{ uri: venueLogo }} style={styles.venueLogo} />
          </View>
        </View>
        <View style={styles.productInfoContainer}>
          <View style={styles.productNameAndPriceContainer}>
            <Text style={styles.productNameText}>{currentProduct?.name}</Text>
            <Text style={[styles.productPriceText, { color: currentVenue?.appColor ?? greenColor }]}>
              {currentProduct?.price ? valueToCurrencyFormat(currentProduct.price, i18n.language) : null}
            </Text>
          </View>
          <Text>{currentProduct?.description}</Text>
        </View>
      </View>

      {currentProduct?.isClothing ? (
        <View style={styles.clothingSizeTypesContainer}>
          {sizeOptions.map((group, index) => (
            <View key={index} style={styles.sizeTypesContainer}>
              {group.map((clothingSize, index) => (
                <TouchableOpacity
                  key={index}
                  style={[
                    styles.sizeType,
                    currentClothingSize.id === clothingSize.id && { borderWidth: 2, borderColor: '#3483fa' },
                  ]}
                  onPress={() => checkSize(clothingSize.id)}
                >
                  <Text style={{ textAlign: 'center' }}>{clothingSize.description}</Text>
                </TouchableOpacity>
              ))}
            </View>
          ))}

          <Text style={{ textAlign: 'center' }}>
            {currentClothingSize.stock === 1
              ? `${t('last-unit-available')}`
              : `${currentClothingSize.stock} ${t('units-available')}`}
          </Text>
        </View>
      ) : null}

      {currentProduct && (
        <View style={styles.guarnicionesExtrasContainer}>
          <GuarnicionExtraItem
            isFromDetail={true}
            currentVenue={currentVenue}
            productId={currentProduct.id}
            selectedGuanicionesAndExtrasFromDetail={selectedGuarnicionesAndExtras}
            onRemoveGuarnicionExtra={(guarnicionExtraDetail) =>
              handleRemoveGuarnicionesAndExtras(guarnicionExtraDetail)
            }
          />
        </View>
      )}

      <View style={styles.commentsContainer}>
        <Text style={styles.commentsTitle}>{t('additional-comments')}</Text>
        <View style={styles.textareaContainer}>
          <TextInput
            placeholder={t('order-notes')}
            multiline={true}
            numberOfLines={4}
            maxLength={100}
            value={getCommentValue()}
            onChangeText={onChangeComment}
            style={styles.textareaStyle}
          />
        </View>
      </View>

      <View style={styles.quantityContainer}>
        <View style={styles.controlQuantitySection}>{renderControlQuantity()}</View>
        <View style={styles.addQuantityContainer}>
          <TouchableOpacity
            style={[styles.addQuantityBtn, { backgroundColor: currentVenue?.appColor ?? greenColor }]}
            onPress={async () => {
              if (currentShoppingCart && currentShoppingCart.processed && !currentShoppingCart.isCash) {
                setModalizeData({
                  content: modalOrderPending(Platform.OS === 'web' ? currentShoppingCart.commerce.code : undefined),
                  closeOnOverlayTap: false,
                });
              } else {
                await addProductToShoppingCart();
              }
            }}
            disabled={!currentCommerce?.enabled || actionsEnabled === false}
          >
            <View style={styles.quantityAndPriceContainer}>
              <Text style={styles.quantityText}>{productQuantity}</Text>
            </View>
            <View style={styles.addToCartContainer}>
              <Text style={styles.addToCartAndPriceText}>{t('add-to-cart')}</Text>
            </View>
            <View style={styles.quantityAndPriceContainer}>
              <Text style={styles.addToCartAndPriceText}>
                {valueToCurrencyFormat(calculateProductPriceAccordingToQuantity() || 0, i18n.language)}
              </Text>
            </View>
          </TouchableOpacity>
        </View>
      </View>

      {currentProduct && [ProductType.WExtra, ProductType.WGuarnicion].includes(currentProduct.typeId) && (
        <GuarnicionesExtrasModal
          items={guarnicionesAndExtras}
          onClose={() => setActionsEnabled(true)}
          visible={actionsEnabled === false}
          product={currentProduct}
          currentVenue={currentVenue}
          onChangeQuantity={undefined}
          onAddGuarnicionExtra={handleAddGuarnicionOrExtra}
        />
      )}
    </ScrollView>
  );
};
